import { Group, ScrollArea, Stack } from '@mantine/core';
import { ChangeEvent, ReactNode } from 'react';
import type { Board, Team } from '../../../../../api/projects-client/projects-client.type';
import type { Epic } from '../../../../../api/tasks-client/task-client.type';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../../../../../ui-library/checkbox/checkbox';
import { Tooltip } from '../../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../../ui-library/typography/typography';
import { MAX_SELECTION } from '../../initiative-performance.constants';

export type FilterItem = Team | Board | Epic;

type Props<T extends FilterItem> = {
  items: T[];
  selectedItems: T[];
  onSelectItems: (items: T[]) => void;
  getItemName: (item: T) => string;
  getItemLabel?: (item: T) => ReactNode;
  getItemId?: (item: T) => string;
  getItemDescription?: (item: T) => ReactNode;
  getItemExtraInfo?: (item: T) => ReactNode;
};

export const FiltersList = <T extends FilterItem>({
  items,
  selectedItems,
  onSelectItems,
  getItemName,
  getItemLabel,
  getItemId = (item) => item.id,
  getItemDescription,
  getItemExtraInfo,
}: Props<T>) => {
  const i18n = useTranslation();
  const handleCheckboxChange = (item: T, event: ChangeEvent<HTMLInputElement>) => {
    const itemId = getItemId(item);
    onSelectItems(
      event.target.checked
        ? [...selectedItems, item]
        : selectedItems.filter((selected) => getItemId(selected) !== itemId),
    );
  };

  return (
    <ScrollArea h="100%" scrollbarSize={0}>
      <Stack gap={24}>
        {items.map((item) => {
          const itemId = getItemId(item);
          const isChecked = selectedItems.some((selected) => getItemId(selected) === itemId);
          const isDisabled = !isChecked && selectedItems.length === MAX_SELECTION;

          const defaultLabel = (
            <Text size="small" style={{ lineHeight: '20px' }}>
              {getItemName(item)}
            </Text>
          );

          return (
            <Tooltip
              key={itemId}
              label={i18n.t('common.tooltips.max_selection_limit', {
                count: MAX_SELECTION,
              })}
              variant="white"
              disabled={!isDisabled}
            >
              <Stack gap={8} key={itemId}>
                <Group justify="space-between">
                  <Checkbox
                    label={getItemLabel ? getItemLabel(item) : defaultLabel}
                    checked={isChecked}
                    onChange={(event) => handleCheckboxChange(item, event)}
                    disabled={isDisabled}
                  />
                  {getItemExtraInfo ? getItemExtraInfo(item) : null}
                </Group>
                {getItemDescription ? getItemDescription(item) : null}
              </Stack>
            </Tooltip>
          );
        })}
      </Stack>
    </ScrollArea>
  );
};
